import type { ReactNode } from 'react';
import { IconButton } from './Button';
import { Tooltip } from './Misc';
import { MoreHorizontal } from 'lucide-react';
import { Menu, MenuContent, MenuItem, MenuTrigger } from './DropdownMenu';

export interface RowAction {
  key: string;
  /** Visible tooltip and accessible name, e.g. "Edit Anita Desai". */
  label: string;
  icon: ReactNode;
  onClick: () => void;
  tone?: 'danger';
  disabled?: boolean;
  /** Explains why the action is unavailable (shown instead of the label). */
  disabledReason?: string;
  loading?: boolean;
  hidden?: boolean;
}

/**
 * Always-visible row actions for data tables: compact icon buttons with tooltips, so View / Edit /
 * Delete never hide behind an overflow menu. Destructive actions use the danger tone.
 */
export function RowActions({ actions, label, compact = false }: { actions: RowAction[]; label?: string; compact?: boolean }) {
  const shown = actions.filter((a) => !a.hidden);
  if (!shown.length) return null;
  if (compact) return <Menu><MenuTrigger asChild><IconButton label={label ?? 'Row actions'} icon={<MoreHorizontal size={18} />} variant="secondary" size="sm" /></MenuTrigger><MenuContent>{shown.map(a => <MenuItem key={a.key} icon={a.icon} onSelect={a.onClick} disabled={a.disabled || a.loading} tone={a.tone} description={a.disabled ? a.disabledReason : undefined}>{a.label}</MenuItem>)}</MenuContent></Menu>;
  return (
    <div className="row-actions" role="group" aria-label={label ?? 'Row actions'}>
      {shown.map((a) => (
        <Tooltip key={a.key} content={a.disabled && a.disabledReason ? a.disabledReason : a.label}>
          <span className="row-action-wrap">
            <IconButton size="sm" variant={a.tone === 'danger' ? 'danger-ghost' : 'ghost'} label={a.label} icon={a.icon} onClick={a.onClick} disabled={a.disabled} loading={a.loading} />
          </span>
        </Tooltip>
      ))}
    </div>
  );
}
